import AccountCircle from '@mui/icons-material/AccountCircle';
import { FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentLanguage, getCurrentUnitSystem, L, setCurrentUnitSystem } from 'harmony-language';
import React from 'react';
import { Link } from 'react-router-dom';
import { useEditContactPreferences } from '../../api/mutations/edit/use-edit-contact-preferences-mutation';
import { useUser } from '../../api/queries/use-user';
import { getTransUserRole } from '../../constants/constants';
import { getTimezone, setTimezone } from '../../utils/date-time-utils';
import { useDraftLoadPreferences } from '../drafts-loads/hooks/useDraftLoadPreferences';
import { CircularLoading } from '../shared/circular-loading';
import { useBoolean } from '../shared/hooks/use-boolean';
import { useUpdateUserPreferences } from '../shared/hooks/use-update-user-preferences';
import { SelectDraftsLoadsDefaultSort } from '../shared/inputs/select-draft-loads-default-sort';
import { SelectLanguage } from '../shared/inputs/select-language';
import { SelectTimezone } from '../shared/inputs/select-timezone';
import { SelectUnits } from '../shared/inputs/select-units';
import { SelectUnitsWeight } from '../shared/inputs/select-units-weight';
import { fetchLanguageDictionary } from './actions/fetch-language-dictionary';
import { userSignOut } from './actions/user-sign-out';
import { useCarrier } from './selectors/use-permissions';
import { UserNotificationPreferences } from './user-notification-preferences';
import { UserResourcePreferences } from './user-resource-preferences';
import { getRoles } from './utils/user-mappings';

const Header = styled('div')({
    paddingBottom: '0.5rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
});

const Roles = styled('div')({
    paddingBottom: '0.5rem',
})

const UserButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.secondary.light,
    transition: '0.3s',
    '&:hover': {
        color: theme.palette.secondary.contrastText
    }
}));

const UserSettingsPopper = styled(Popper)(({ theme }) => ({
    left: `${theme.spacing(2)} !important`,
    zIndex: 50
}));

const StyledLink = styled(Link)(({ theme }) => ({
    textDecoration: 'none',
    transition: '0.3s',
    color: theme.palette.secondary.light,
    '&:hover': {
        color: theme.palette.secondary.dark,
    },
}));

export const UserInformation = () => {
    const { user } = useUser();
    const [anchorElement, setAnchorElement] = React.useState(null);
    const [isNotificationPrefsOpen, setNotificationPrefsOpen, setNotificationPrefsClosed] = useBoolean(false);
    const [isResourcePrefsOpen, setResourcePrefsOpen, setResourcePrefsClosed] = useBoolean(false);
    const [isLoading, loading, done] = useBoolean(false);
    const currentUserIsCarrier = useCarrier();
    const { mutate: mutateContactPreferences } = useEditContactPreferences(user.organizationId, user.contact.id);
    const queryClient = useQueryClient();
    const { updatePreferences } = useUpdateUserPreferences();
    const { draftsLoadsDefaultSort, showLoadDate, estimatedTotalTripMode, showTractor, showTrailer } = useDraftLoadPreferences();

    const updateContactPreferences = (preference) => {
        const contact = user.contact;
        const newContact = {
            ...contact,
            ...preference,
        }
        mutateContactPreferences(newContact);
    }

    const languageChanged = async (value) => {
        loading();
        updateContactPreferences({ language: value });
        fetchLanguageDictionary(value);
        done();
    };

    const timezoneChanged = async (value) => {
        loading();
        updateContactPreferences({ timezone: value });
        setTimezone(value);
        done();
    };

    const unitsChanged = async (value) => {
        loading();
        updateContactPreferences({ units: value });
        setCurrentUnitSystem(value);
        done();
    };

    const unitsWeightChanged = async (value) => {
        loading();
        updateContactPreferences({ unitsWeight: value });
        done();
    };

    const handleDefaultSortChange = (newVal) => {
        const newPreferences = {
            ...user.preferences,
            draftsLoadsDefaultSort: newVal,
        }
        updatePreferences(newPreferences);
    }

    const handleSwitchChange = (key, currentVal) => {
        const newPreferences = {
            ...user.preferences,
            [key]: !currentVal,
        }
        updatePreferences(newPreferences);
    }

    const handleEstimatedTotalTripToggle = (_, newValue) => {
        const newPreferences = {
            ...user.preferences,
            estimatedTotalTripMode: newValue,
        }
        updatePreferences(newPreferences);
    }

    return (
        <>
            {isNotificationPrefsOpen && <UserNotificationPreferences contactId={user.contactId} organizationId={user.organizationId} isOpen={isNotificationPrefsOpen} closePreferences={setNotificationPrefsClosed} />}
            {isResourcePrefsOpen && <UserResourcePreferences organizationId={999} isOpen={isResourcePrefsOpen} closePreferences={setResourcePrefsClosed} /> }
            <ClickAwayListener
                mouseEvent='onMouseDown'
                touchEvent='onTouchStart'
                onClickAway={() => setAnchorElement(null)}
            >
                <div>
                    <UserButton
                        onClick={(e) => setAnchorElement(anchorElement ? null : e.currentTarget)}
                        aria-label='User Settings'
                        size='large'>
                        <AccountCircle fontSize='large' />
                    </UserButton>
                    <UserSettingsPopper open={Boolean(anchorElement)} anchorEl={anchorElement}
                        placement='right-end'>
                        <Paper variant='outlined' sx={{ padding: '.5rem', width: '25vw'}}>
                            <Header>
                                <Typography variant='h6'>{user.name}</Typography>
                                <StyledLink to='/login' onClick={() => userSignOut(queryClient)}>{L.signOut()}</StyledLink>
                            </Header>
                            <Roles>{user.organization.name}</Roles>
                            <Roles>{getRoles(user).map(role => getTransUserRole(role).label).join(', ')}</Roles>
                            <SelectLanguage onChange={languageChanged} language={getCurrentLanguage()}
                                disabled={isLoading} />
                            <SelectTimezone onChange={timezoneChanged} timezone={getTimezone()}
                                disabled={isLoading} />
                            <SelectUnits onChange={unitsChanged} units={getCurrentUnitSystem()}
                                disabled={isLoading} />
                            <SelectUnitsWeight onChange={unitsWeightChanged} unitsWeight={user.contact.unitsWeight} 
                                disabled={isLoading} />
                            <SelectDraftsLoadsDefaultSort
                                onChange={handleDefaultSortChange}
                                desc={draftsLoadsDefaultSort}
                            />
                            <Typography style={{ marginRight: '.4rem', display: 'inline'}}>{L.estimatedTotalTripColumn()}</Typography>
                            <ToggleButtonGroup
                                color="primary"
                                value={estimatedTotalTripMode}
                                size='small'
                                style={{ marginTop: '.5rem' }}
                                exclusive
                                onChange={handleEstimatedTotalTripToggle}
                                aria-label="Platform"
                            >
                                <ToggleButton value="none">{L.off()}</ToggleButton>
                                <ToggleButton value="time">{L.time()}</ToggleButton>
                                <ToggleButton value="distance">{L.distance()}</ToggleButton>
                            </ToggleButtonGroup>
                            <FormControlLabel
                                sx={{ display: 'block' }}
                                control={
                                    <Switch
                                        checked={showLoadDate}
                                        onChange={() => handleSwitchChange('showLoadDate', showLoadDate)}
                                    />
                                }
                                label={L.showLoadDate()}
                            />
                            <FormControlLabel
                                sx={{ display: 'block' }}
                                control={
                                    <Switch
                                        checked={showTractor}
                                        onChange={() => handleSwitchChange('showTractor', showTractor)}
                                    />
                                }
                                label={L.showTractor()}
                            />
                            <FormControlLabel
                                sx={{ display: 'block' }}
                                control={
                                    <Switch
                                        checked={showTrailer}
                                        onChange={() => handleSwitchChange('showTrailer', showTrailer)}
                                    />
                                }
                                label={L.showTrailer()}
                            />
                            {isLoading && <CircularLoading />}
                            <div>
                                {!currentUserIsCarrier ? <Button variant='contained' style={{ marginTop: '1rem', marginRight: '8px' }} onClick={() => {
                                    setAnchorElement(null);
                                    setNotificationPrefsOpen();
                                }}>{L.notificationPreferences()}</Button> : null}

                                <Button variant='contained' style={{ marginTop: '1rem' }} onClick={() => {
                                    setAnchorElement(null);
                                    setResourcePrefsOpen();
                                }}>{L.resourcePreferences()}</Button>
                            </div>
                        </Paper>
                    </UserSettingsPopper>
                </div>
            </ClickAwayListener>
        </>
    );
};
